import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Zap, Headphones } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useContentText } from "../contexts/ContentContext";
import { ReptileCard } from "../components/ReptileCard";
import { BreederCard } from "../components/BreederCard";
import { SunStone, StepGlyph, FretDivider, SerpentMark } from "../components/AztecOrnaments";

const Home = () => {
  const { t, lang } = useLang();
  const { content, cl } = useContentText();
  const [featured, setFeatured] = useState([]);
  const [breeders, setBreeders] = useState([]);

  useEffect(() => {
    api.get("/reptiles", { params: { featured: true, limit: 8 } })
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatured([]));
    api.get("/breeders").then((r) => setBreeders(r.data)).catch(() => setBreeders([]));
  }, []);

  const hero = content?.hero || {};
  const heroImg = hero.image;
  const manifesto = content?.manifesto || {};
  const manifestoImg = manifesto.image || heroImg;
  const categories = (content?.categories?.items || []).map((c, i) => ({
    key: c.key || `cat-${i}`,
    label: lang === "es" ? c.label_es : c.label_en,
    href: c.href || "/marketplace",
    img: c.image,
    cta: lang === "es" ? (c.cta_es || "Ver colección") : (c.cta_en || "View collection"),
  }));

  const trust = [
    { icon: ShieldCheck, label: cl("hero", "trust_1", t("home.trustVerified")), tone: "emerald", code: "01" },
    { icon: Sparkles, label: cl("hero", "trust_2", t("home.trustRare")), tone: "gold", code: "02" },
    { icon: Zap, label: cl("hero", "trust_3", t("home.trustShipping")), tone: "emerald", code: "03" },
    { icon: Headphones, label: cl("hero", "trust_4", t("home.trustSupport")), tone: "gold", code: "04" },
  ];

  return (
    <div data-testid="home-page" className="bg-black">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[92vh] overflow-hidden obsidian-base">

        {/* Cinematic snake — center bleed, full background */}
        <div className="absolute inset-0 pointer-events-none">
          {heroImg && (
            <img
              src={heroImg}
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
              style={{ filter: "contrast(1.15) brightness(0.65) saturate(0.85)" }}
            />
          )}
          <div className="serpent-glow" style={{ top: "40%", left: "50%", transform: "translate(-50%, -50%)", width: "320px", height: "320px" }} />
        </div>

        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.55) 45%, #000 80%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />

        <SunStone size={900} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block" style={{ color: "rgba(212, 175, 55, 0.05)" }} />

        <div className="hidden xl:flex absolute left-5 top-1/2 -translate-y-1/2 flex-col items-center gap-6 z-10">
          <div className="w-px h-20 bg-gradient-to-b from-transparent via-[var(--monzal-gold)]/40 to-transparent" />
          <div className="vertical-label text-[var(--monzal-gold)]/60">MONZAL · MMXXV</div>
          <div className="w-px h-20 bg-gradient-to-b from-transparent via-[var(--monzal-gold)]/40 to-transparent" />
        </div>

        <div className="hidden xl:flex absolute right-5 top-1/2 -translate-y-1/2 flex-col items-center gap-6 z-10">
          <div className="vertical-label text-[var(--monzal-emerald)]/60">QUETZALCOATL · GENESIS</div>
        </div>

        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-12 pt-20 lg:pt-28 pb-24 z-10">
          <div className="max-w-3xl mx-auto text-center flex flex-col items-center">

            <div className="flex items-center gap-4 mb-10 fade-up justify-center" data-testid="home-eyebrow">
              <div className="h-px flex-1 max-w-[80px] bg-gradient-to-l from-[var(--monzal-gold)]/60 to-transparent" />
              <SerpentMark size={36} className="text-[var(--monzal-gold)]/70" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[var(--monzal-gold)] whitespace-nowrap">
                {cl("hero", "eyebrow", t("home.eyebrow"))}
              </span>
              <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-[var(--monzal-gold)]/60 to-transparent" />
            </div>

            <h1 className="font-display tracking-[-0.02em] leading-[0.86] fade-up" data-testid="home-headline">
              <span className="block font-light text-bone text-[3rem] sm:text-[4.5rem] lg:text-[6.5rem] uppercase">
                {cl("hero", "headline_1", "Sangre")}
              </span>
              <span className="block font-light text-bone text-[3rem] sm:text-[4.5rem] lg:text-[6.5rem] uppercase">
                {cl("hero", "headline_2", "Antigua.")}
              </span>
              <span className="block font-bold text-metal-gold text-[3rem] sm:text-[4.5rem] lg:text-[6.5rem] uppercase mt-2">
                {cl("hero", "headline_3", "Futuro")}
              </span>
              <span className="block font-bold text-metal-gold text-[3rem] sm:text-[4.5rem] lg:text-[6.5rem] uppercase">
                {cl("hero", "headline_4", "Exótico.")}
              </span>
            </h1>

            <div className="mt-10 mb-7 fade-up">
              <FretDivider width={300} className="text-[var(--monzal-gold)]/50" />
            </div>

            <p className="text-gray-300 text-base lg:text-lg leading-relaxed max-w-xl font-light fade-up mx-auto">
              {cl("hero", "subtitle", t("home.sub"))}
            </p>

            <div className="flex flex-wrap gap-4 mt-10 fade-up justify-center">
              <Link to={hero.cta_primary_href || "/marketplace"} className="btn-gold" data-testid="home-cta-marketplace">
                {cl("hero", "cta_primary", t("home.ctaExplore"))} <ArrowRight size={14} />
              </Link>
              <Link to={hero.cta_secondary_href || "/shop"} className="btn-ghost" data-testid="home-cta-shop">
                {cl("hero", "cta_secondary", t("home.ctaShop"))} <ArrowRight size={12} />
              </Link>
            </div>
          </div>

          <div className="mt-20 lg:mt-28 fade-up max-w-5xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
              {trust.map(({ icon: Icon, label, tone, code }) => (
                <div
                  key={code}
                  className="bg-black/70 backdrop-blur-sm p-5 lg:p-6 relative group text-center"
                  data-testid={`trust-${code}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <Icon size={18} className={tone === "gold" ? "text-[var(--monzal-gold)]" : "text-[var(--monzal-emerald)]"} />
                    <span className="font-mono-tech text-[10px] text-gray-600 tracking-widest">·{code}</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-display text-gray-100 leading-snug text-left">
                    {label}
                  </div>
                  <div className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full bg-[var(--monzal-gold)] transition-all duration-500" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 aztec-fret z-10" />
      </section>

      {/* ============ MANIFESTO STRIP ============ */}
      <section className="relative border-t border-white/5 bg-gradient-to-b from-black to-[#040506]">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-center lg:text-left mx-auto lg:mx-0">
            <div className="flex items-center gap-3 mb-6 justify-center lg:justify-start">
              <StepGlyph size={28} className="text-[var(--monzal-gold)]/60" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[var(--monzal-gold)]">
                {cl("manifesto", "eyebrow", "MANIFESTO · I")}
              </span>
            </div>
            <h2 className="font-display font-light text-3xl lg:text-5xl text-white tracking-tight leading-tight whitespace-pre-line">
              {cl("manifesto", "title", t("home.manifestoTitle"))}
            </h2>
            <p className="text-gray-400 mt-8 max-w-2xl leading-relaxed text-base lg:text-lg mx-auto lg:mx-0">
              {cl("manifesto", "body", t("home.manifestoBody"))}
            </p>
            <div className="mt-12 grid grid-cols-3 gap-px bg-white/5 border border-white/5 max-w-2xl mx-auto lg:mx-0">
              <div className="bg-black p-5">
                <div className="font-display text-3xl lg:text-4xl text-metal-gold">{manifesto.stat_1_value || "99.7%"}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-2">{cl("manifesto", "stat_1_label", "Salud verificada")}</div>
              </div>
              <div className="bg-black p-5">
                <div className="font-display text-3xl lg:text-4xl text-metal-gold">{manifesto.stat_2_value || "III"}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-2">{cl("manifesto", "stat_2_label", "Generaciones")}</div>
              </div>
              <div className="bg-black p-5">
                <div className="font-display text-3xl lg:text-4xl text-metal-gold">{breeders.length || 0}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-2">{cl("manifesto", "stat_3_label", "Criadores Selectos")}</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[3/4] overflow-hidden border border-[var(--monzal-border-gold)] relative">
              {manifestoImg && (
                <img src={manifestoImg} alt="" className="w-full h-full object-cover" style={{ filter: "contrast(1.2) brightness(0.85)" }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-transparent to-emerald-900/10" />
              <span className="gold-bracket-tl" />
              <span className="gold-bracket-tr" />
              <span className="gold-bracket-bl" />
              <span className="gold-bracket-br" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-black border border-[var(--monzal-border-gold)] px-4 py-3">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] text-[var(--monzal-gold)]">
                {cl("manifesto", "badge", "MONZAL · GENETICS LAB")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CATEGORIES ============ */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-12 py-20 lg:py-28 border-t border-white/5">
        <div className="flex flex-col items-center text-center mb-10 lg:mb-14 gap-3">
          <div className="flex items-center gap-3">
            <StepGlyph size={22} className="text-[var(--monzal-gold)]/60" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[var(--monzal-gold)]">
              {cl("categories", "eyebrow", t("home.categoriesEyebrow"))}
            </span>
            <StepGlyph size={22} className="text-[var(--monzal-gold)]/60 scale-x-[-1]" />
          </div>
          <h2 className="font-display font-light text-3xl lg:text-5xl text-white tracking-tight">
            {cl("categories", "title", t("home.categoriesTitle"))}
          </h2>
          <Link to="/marketplace" className="text-xs uppercase tracking-[0.22em] font-display text-[var(--monzal-gold)] hover:text-white inline-flex items-center gap-2 mt-2" data-testid="categories-view-all">
            {t("home.viewAll")} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
          {categories.map((c, i) => (
            <Link
              key={c.key}
              to={c.href}
              data-testid={`category-card-${c.key}`}
              className="group relative h-[320px] lg:h-[460px] overflow-hidden image-zoom fade-up bg-black border border-white/5 hover:border-[var(--monzal-gold)]/40 transition-colors duration-500"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              {c.img && (
                <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-90 transition duration-700" style={{ filter: "contrast(1.15) saturate(0.7)" }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <span className="gold-bracket-tl opacity-0 group-hover:opacity-100 transition" />
              <span className="gold-bracket-br opacity-0 group-hover:opacity-100 transition" />
              <div className="relative h-full flex flex-col justify-between p-5 lg:p-6">
                <div className="flex items-start justify-between">
                  <span className="font-mono-tech text-[10px] tracking-[0.3em] text-[var(--monzal-gold)]/70">·0{i+1}</span>
                </div>
                <div>
                  <h3 className="font-display font-medium text-lg lg:text-2xl text-white tracking-tight uppercase">
                    {c.label}
                  </h3>
                  <div className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-display text-[var(--monzal-emerald)] group-hover:gap-3 transition-all">
                    {c.cta} <ArrowRight size={11} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ FEATURED ============ */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-12 py-20 lg:py-28 border-t border-white/5">
        <div className="flex flex-col items-center text-center mb-12 gap-3">
          <div className="flex items-center gap-3">
            <StepGlyph size={22} className="text-[var(--monzal-gold)]/60" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[var(--monzal-gold)]">
              {cl("featured", "eyebrow", t("home.featuredEyebrow"))}
            </span>
            <StepGlyph size={22} className="text-[var(--monzal-gold)]/60 scale-x-[-1]" />
          </div>
          <h2 className="font-display font-light text-3xl lg:text-5xl text-white tracking-tight">
            {cl("featured", "title", t("home.featuredTitle"))}
          </h2>
          <p className="text-gray-400 max-w-xl mt-2">{cl("featured", "subtitle", t("home.featuredSub"))}</p>
          <Link to="/marketplace" className="text-sm uppercase tracking-[0.22em] font-display text-[var(--monzal-gold)] hover:text-white inline-flex items-center gap-2 mt-3" data-testid="featured-view-all">
            {t("home.viewAll")} <ArrowRight size={14} />
          </Link>
        </div>

        {featured.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.slice(0, 4).map((r, i) => (
              <div key={r.id} className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <ReptileCard reptile={r} testIdPrefix="featured" />
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-white/10 py-20 px-6 text-center max-w-2xl mx-auto">
            <p className="font-mono-tech text-[11px] tracking-[0.3em] uppercase text-gray-500">Drop en preparación</p>
            <p className="text-gray-400 mt-4 max-w-md mx-auto whitespace-pre-line">
              {cl("featured", "empty", "Próximos ejemplares en camino.")}
            </p>
          </div>
        )}
      </section>

      {/* ============ BREEDERS ============ */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-12 py-20 lg:py-28 border-t border-white/5">
        <div className="flex flex-col items-center text-center mb-12 gap-3">
          <div className="flex items-center gap-3">
            <StepGlyph size={22} className="text-[var(--monzal-gold)]/60" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[var(--monzal-gold)]">
              {cl("breeders_section", "eyebrow", t("nav.breeders"))}
            </span>
            <StepGlyph size={22} className="text-[var(--monzal-gold)]/60 scale-x-[-1]" />
          </div>
          <h2 className="font-display font-light text-3xl lg:text-5xl text-white tracking-tight">
            {cl("breeders_section", "title", t("home.breedersTitle"))}
          </h2>
          <p className="text-gray-400 max-w-xl mt-2">{cl("breeders_section", "subtitle", t("home.breedersSub"))}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {breeders.map((b) => <BreederCard key={b.id} breeder={b} />)}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-12 py-24">
        <div className="border border-[var(--monzal-border-gold)] p-10 lg:p-16 relative overflow-hidden bg-gradient-to-br from-black via-[#080808] to-black">
          <div className="absolute inset-0 grid-lines opacity-20" />
          <SunStone size={500} className="absolute -right-32 -top-32" style={{ color: "rgba(212, 175, 55, 0.05)" }} />
          <span className="gold-bracket-tl" />
          <span className="gold-bracket-tr" />
          <span className="gold-bracket-bl" />
          <span className="gold-bracket-br" />
          <div className="relative flex flex-col items-center text-center gap-8">
            <div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <SerpentMark size={28} className="text-[var(--monzal-emerald)]/70" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[var(--monzal-emerald)]">
                  {cl("final_cta", "eyebrow", "SIGNAL · 01")}
                </span>
              </div>
              <h2 className="font-display font-light text-3xl lg:text-5xl text-white tracking-tight">
                {cl("final_cta", "title", t("home.footerCta"))}
              </h2>
              <p className="text-gray-400 mt-4 max-w-lg mx-auto">{cl("final_cta", "subtitle", t("home.footerCtaSub"))}</p>
            </div>
            <Link to={content?.final_cta?.cta_href || "/marketplace"} data-testid="home-final-cta" className="btn-emerald-glow">
              {cl("final_cta", "cta", t("home.ctaExplore"))} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
